import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../../services/apiClient";
import { getCookie } from "../../utils/cookies";
import toast from "../../utils/toast";

function formatAmount(amount) {
  const num = Number(amount) || 0;
  return `₹${num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function StatusBadge({ status }) {
  const isActive = String(status).toLowerCase() === "active";
  return (
    <span className={`badge ${isActive ? "green" : "red"}`}>
      {status || "-"}
    </span>
  );
}

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function formatDateForApi(dateStr) {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-");
  return `${day}-${MONTH_NAMES[Number(month) - 1]}-${year}`;
}

const LEVEL_OPTIONS = Array.from({ length: 15 }, (_, i) => i + 1);
const PAGE_SIZE = 10;

function Pagination({ currentPage, totalItems, pageSize, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  if (totalPages <= 1) return null;

  return (
    <div className="pagination-row">
      <button
        className="btn-sm"
        disabled={currentPage === 1}
        onClick={() => onPageChange(currentPage - 1)}
      >
        Prev
      </button>
      <span className="pagination-info">
        Page {currentPage} of {totalPages}
      </span>
      <button
        className="btn-sm"
        disabled={currentPage === totalPages}
        onClick={() => onPageChange(currentPage + 1)}
      >
        Next
      </button>
    </div>
  );
}

export default function TeamReports() {
  const navigate = useNavigate();
  const [isAccountActive, setIsAccountActive] = useState(false);
  const [activeTab, setActiveTab] = useState("direct");
  const [isTeamSponserReportData, setTeamSponserReportData] = useState();
  const [isTeamDirectReportData, setTeamDirectReportData] = useState();
  const [isDirectLoading, setDirectLoading] = useState(true);
  const [isSponserLoading, setSponserLoading] = useState(false);

  const [directFromDate, setDirectFromDate] = useState("");
  const [directToDate, setDirectToDate] = useState("");

  const [sponserFromDate, setSponserFromDate] = useState("");
  const [sponserToDate, setSponserToDate] = useState("");
  const [sponserLevelNo, setSponserLevelNo] = useState(0);
  const [sponserSearchUserId, setSponserSearchUserId] = useState(() => {
    const userData = JSON.parse(getCookie("userData") || "{}");
    return userData.userID || "";
  });

  const [directPage, setDirectPage] = useState(1);
  const [sponserPage, setSponserPage] = useState(1);

  const [isIncomeReportData, setIncomeReportData] = useState();
  const [isIncomeLoading, setIncomeLoading] = useState(false);
  const [incomeFromDate, setIncomeFromDate] = useState("");
  const [incomeToDate, setIncomeToDate] = useState("");
  const [incomePage, setIncomePage] = useState(1);

  const todayDate = new Date().toISOString().slice(0, 10);

  const [isFundRequestReportData, setFundRequestReportData] = useState();
  const [isFundRequestLoading, setFundRequestLoading] = useState(false);
  const [fundRequestFromDate, setFundRequestFromDate] = useState(todayDate);
  const [fundRequestToDate, setFundRequestToDate] = useState(todayDate);
  const [fundRequestAccountNo, setFundRequestAccountNo] = useState("");
  const [fundRequestPage, setFundRequestPage] = useState(1);

  const [isWithdrawReportData, setWithdrawReportData] = useState();
  const [isWithdrawLoading, setWithdrawLoading] = useState(false);
  const [withdrawFromDate, setWithdrawFromDate] = useState(todayDate);
  const [withdrawToDate, setWithdrawToDate] = useState(todayDate);
  const [withdrawPage, setWithdrawPage] = useState(1);

  useEffect(() => {
    fetchDirectTeamReportData(true);
    fetchAccountStatus();
  }, []);

  const fetchAccountStatus = async () => {
    try {
      const userData = JSON.parse(getCookie("userData") || "{}");
      const res = await apiClient.post("/Dashboard/MataMaskDashboard", {
        appid: "ROUNDPAYAPPID13APR20191351",
        imei: "d593bdd26dfbea56",
        loginTypeID: 1,
        regKey: "",
        serialNo: "d593bdd26dfbea56",
        session: getCookie("session") || "",
        sessionID: getCookie("sessionID") || "",
        userID: userData.userID || "",
        version: "1.0",
      });

      if (res.statuscode == 1) {
        setIsAccountActive(res.data?.isTopup === true);
      }
    } catch (err) {
      console.log("error", err);
    }
  };

  const fetchSponserTeamReportData = async (
    isRecent = false,
    overrides = {},
  ) => {
    setSponserLoading(true);
    try {
      const userData = JSON.parse(getCookie("userData") || "{}");

      const fromDate = overrides.fromDate ?? sponserFromDate;
      const toDate = overrides.toDate ?? sponserToDate;
      const levelNo = overrides.levelNo ?? sponserLevelNo;
      const searchUserId = overrides.searchUserId ?? sponserSearchUserId;

      const res = await apiClient.post("/Dashboard/SponserList", {
        appSession: {
          appid: "ROUNDPAYAPPID13APR20191351",
          imei: "d593bdd26dfbea56",
          loginTypeID: "1",
          regKey: "",
          serialNo: "d593bdd26dfbea56",
          version: "1.0",
          uid: 0,
          session: getCookie("session") || "",
          sessionID: getCookie("sessionID") || "",
          userID: userData.userID || "",
        },
        Request: {
          FromDate: formatDateForApi(fromDate),
          ToDate: formatDateForApi(toDate),
          IsRecent: isRecent,
          LevelNo: levelNo,
          Leg: "All",
          SearchType: "0",
          SearchUserId: searchUserId,
          Status: "All",
        },
      });

      if (res.statuscode == 1) {
        setTeamSponserReportData(res);
      } else {
        setTeamSponserReportData({ data: [] });
        toast.error(res.msg || "No data found");
      }
    } catch (err) {
      console.log("error", err);
    } finally {
      setSponserLoading(false);
    }
  };

  const fetchDirectTeamReportData = async (
    isRecent = false,
    overrides = {},
  ) => {
    setDirectLoading(true);
    try {
      const userData = JSON.parse(getCookie("userData") || "{}");

      const fromDate = overrides.fromDate ?? directFromDate;
      const toDate = overrides.toDate ?? directToDate;

      const res = await apiClient.post("/Dashboard/DirectBusiness", {
        appSession: {
          appid: "ROUNDPAYAPPID13APR20191351",
          imei: "d593bdd26dfbea56",
          loginTypeID: "1",
          regKey: "",
          serialNo: "d593bdd26dfbea56",
          version: "1.0",
          uid: 0,
          session: getCookie("session") || "",
          sessionID: getCookie("sessionID") || "",
          userID: userData.userID || "",
        },
        Request: {
          FromDate: formatDateForApi(fromDate),
          ToDate: formatDateForApi(toDate),
          IsRecent: isRecent,
          BusinessType: "0",
          Leg: "",
          LevelNo: 0,
          SearchType: 0,
        },
      });

      if (res.statuscode == 1) {
        setTeamDirectReportData(res);
      } else {
        setTeamDirectReportData({ data: [] });
        toast.error(res.msg || "No data found");
      }
    } catch (err) {
      console.log("error", err);
    } finally {
      setDirectLoading(false);
    }
  };

  const fetchIncomeReportData = async (isRecent = false, overrides = {}) => {
    setIncomeLoading(true);
    try {
      const userData = JSON.parse(getCookie("userData") || "{}");

      const fromDate = overrides.fromDate ?? incomeFromDate;
      const toDate = overrides.toDate ?? incomeToDate;

      const res = await apiClient.post("/App/GetIncomeWiseReport", {
        FromDate: formatDateForApi(fromDate),
        ToDate: formatDateForApi(toDate),
        IsRecent: isRecent,
        LevelNo: "1",
        OPID: 0,
        incomeCategoryID: 1,
        isSeller: false,
        appid: "ROUNDPAYAPPID13APR20191351",
        imei: "d593bdd26dfbea56",
        loginTypeID: "1",
        regKey: "",
        serialNo: "d593bdd26dfbea56",
        version: "1.0",
        uid: 0,
        session: getCookie("session") || "",
        sessionID: getCookie("sessionID") || "",
        userID: userData.userID || "",
      });

      if (res.statuscode == 1) {
        setIncomeReportData(res);
      } else {
        setIncomeReportData({ incomeWiseReport: [] });
        toast.error(res.msg || "No data found");
      }
    } catch (err) {
      console.log("error", err);
    } finally {
      setIncomeLoading(false);
    }
  };

  const fetchFundRequestReportData = async (overrides = {}) => {
    setFundRequestLoading(true);
    try {
      const userData = JSON.parse(getCookie("userData") || "{}");

      const fromDate = overrides.fromDate ?? fundRequestFromDate;
      const toDate = overrides.toDate ?? fundRequestToDate;
      const accountNo = overrides.accountNo ?? fundRequestAccountNo;

      const res = await apiClient.post("/App/FundOrderReport", {
        WalletTypeID: 0,
        accountNo: accountNo,
        fromDate: formatDateForApi(fromDate),
        isExport: "false",
        isSelf: "true",
        oid: "50",
        status: "0",
        tMode: "0",
        toDate: formatDateForApi(toDate),
        topRows: "100",
        transactionID: "",
        uMobile: "",
        ID: 0,
        IsB2C: false,
        TID: 0,
        appid: "ROUNDPAYAPPID13APR20191351",
        id: 0,
        imei: "d593bdd26dfbea56",
        loginTypeID: 1,
        regKey: "",
        serialNo: "d593bdd26dfbea56",
        session: getCookie("session") || "",
        sessionID: getCookie("sessionID") || "",
        uid: 0,
        userID: userData.userID || "",
        version: "1.0",
      });

      console.log("FundOrderReport response", res);

      if (res.statuscode == 1) {
        setFundRequestReportData(res);
      } else {
        setFundRequestReportData({ fundOrderReport: [] });
        toast.error(res.msg || "No data found");
      }
    } catch (err) {
      console.log("error", err);
    } finally {
      setFundRequestLoading(false);
    }
  };

  const fetchWithdrawReportData = async (isRecent = false, overrides = {}) => {
    setWithdrawLoading(true);
    try {
      const userData = JSON.parse(getCookie("userData") || "{}");

      const fromDate = overrides.fromDate ?? withdrawFromDate;
      const toDate = overrides.toDate ?? withdrawToDate;

      const res = await apiClient.post("/App/DMTReport", {
        IsRecent: isRecent,
        apiid: "0",
        childMobile: "",
        fromDate: formatDateForApi(fromDate),
        isExport: false,
        status: 0,
        toDate: formatDateForApi(toDate),
        topRows: "50",
        ID: 0,
        IsB2C: false,
        TID: 0,
        appid: "ROUNDPAYAPPID13APR20191351",
        id: 0,
        imei: "d593bdd26dfbea56",
        loginTypeID: 1,
        regKey: "",
        serialNo: "d593bdd26dfbea56",
        session: getCookie("session") || "",
        sessionID: getCookie("sessionID") || "",
        uid: 0,
        userID: userData.userID || "",
        version: "1.0",
      });

      if (res.statuscode == 1) {
        setWithdrawReportData(res);
      } else {
        setWithdrawReportData({ dmtReport: [] });
        toast.error(res.msg || "No data found");
      }
    } catch (err) {
      console.log("error", err);
    } finally {
      setWithdrawLoading(false);
    }
  };

  function handleWithdrawTabClick() {
    setActiveTab("withdraw");
    if (!isWithdrawReportData) {
      fetchWithdrawReportData(true);
    }
  }

  function handleWithdrawSearch() {
    setWithdrawPage(1);
    fetchWithdrawReportData(false);
  }

  function handleWithdrawReset() {
    setWithdrawFromDate(todayDate);
    setWithdrawToDate(todayDate);
    setWithdrawPage(1);
    fetchWithdrawReportData(true, { fromDate: todayDate, toDate: todayDate });
  }

  function handleFundRequestTabClick() {
    setActiveTab("fundRequest");
    if (!isFundRequestReportData) {
      fetchFundRequestReportData();
    }
  }

  function handleFundRequestSearch() {
    setFundRequestPage(1);
    fetchFundRequestReportData();
  }

  function handleFundRequestReset() {
    setFundRequestFromDate(todayDate);
    setFundRequestToDate(todayDate);
    setFundRequestAccountNo("");
    setFundRequestPage(1);
    fetchFundRequestReportData({
      fromDate: todayDate,
      toDate: todayDate,
      accountNo: "",
    });
  }

  function handleIncomeTabClick() {
    setActiveTab("income");
    if (!isIncomeReportData) {
      fetchIncomeReportData(true);
    }
  }

  function handleIncomeSearch() {
    setIncomePage(1);
    fetchIncomeReportData(false);
  }

  function handleIncomeReset() {
    setIncomeFromDate("");
    setIncomeToDate("");
    setIncomePage(1);
    fetchIncomeReportData(true, { fromDate: "", toDate: "" });
  }

  function handleDirectSearch() {
    setDirectPage(1);
    fetchDirectTeamReportData(false);
  }

  function handleDirectReset() {
    setDirectFromDate("");
    setDirectToDate("");
    setDirectPage(1);
    fetchDirectTeamReportData(true, { fromDate: "", toDate: "" });
  }

  function handleSponserTabClick() {
    setActiveTab("sponser");
    if (!isTeamSponserReportData) {
      fetchSponserTeamReportData(true);
    }
  }

  function handleSponserSearch() {
    setSponserPage(1);
    fetchSponserTeamReportData(false);
  }

  function handleSponserReset() {
    const userData = JSON.parse(getCookie("userData") || "{}");
    const defaultSearchUserId = userData.userID || "";
    setSponserFromDate("");
    setSponserToDate("");
    setSponserLevelNo(0);
    setSponserSearchUserId(defaultSearchUserId);
    setSponserPage(1);
    fetchSponserTeamReportData(true, {
      fromDate: "",
      toDate: "",
      levelNo: 0,
      searchUserId: defaultSearchUserId,
    });
  }

  const directRows = isTeamDirectReportData?.data || [];
  const sponserRows = isTeamSponserReportData?.data || [];
  const incomeRows = isIncomeReportData?.incomeWiseReport || [];
  const fundRequestRows = isFundRequestReportData?.fundOrderReport || [];
  const withdrawRows = isWithdrawReportData?.dmtReport || [];

  const directPaginated = directRows.slice(
    (directPage - 1) * PAGE_SIZE,
    directPage * PAGE_SIZE,
  );
  const sponserPaginated = sponserRows.slice(
    (sponserPage - 1) * PAGE_SIZE,
    sponserPage * PAGE_SIZE,
  );
  const incomePaginated = incomeRows.slice(
    (incomePage - 1) * PAGE_SIZE,
    incomePage * PAGE_SIZE,
  );
  const fundRequestPaginated = fundRequestRows.slice(
    (fundRequestPage - 1) * PAGE_SIZE,
    fundRequestPage * PAGE_SIZE,
  );
  const withdrawPaginated = withdrawRows.slice(
    (withdrawPage - 1) * PAGE_SIZE,
    withdrawPage * PAGE_SIZE,
  );

  return (
    <>
      <div className="content">
        <section className="page active" id="page-team">
          <div
            id="teamLocked"
            className="content-lock"
            style={{ display: isAccountActive ? "none" : "flex" }}
          >
            <div className="icon-circle">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <rect x={4} y={10} width={16} height={10} rx={2} />
                <path d="M8 10V7a4 4 0 018 0v3" />
              </svg>
            </div>
            <h3>Team reports are locked</h3>
            <p>
              Activate your account to start building your team and view
              detailed reports.
            </p>
            <button
              className="btn-primary"
              style={{ width: "auto", padding: "11px 22px" }}
              onClick={() => navigate("/panel/activate_package")}
            >
              Activate Now
            </button>
          </div>
          <div
            id="teamUnlocked"
            style={{ display: isAccountActive ? "block" : "none" }}
          >
            <div className="team-tabs">
              <button
                className={`team-tab${activeTab === "direct" ? " active" : ""}`}
                onClick={() => setActiveTab("direct")}
              >
                Direct Team
                <span className="team-tab-count">{directRows.length}</span>
              </button>
              <button
                className={`team-tab${activeTab === "sponser" ? " active" : ""}`}
                onClick={handleSponserTabClick}
              >
                Sponsor Team
                {isTeamSponserReportData && (
                  <span className="team-tab-count">{sponserRows.length}</span>
                )}
              </button>
              <button
                className={`team-tab${activeTab === "income" ? " active" : ""}`}
                onClick={handleIncomeTabClick}
              >
                Income
                {isIncomeReportData && (
                  <span className="team-tab-count">{incomeRows.length}</span>
                )}
              </button>
              <button
                className={`team-tab${activeTab === "fundRequest" ? " active" : ""}`}
                onClick={handleFundRequestTabClick}
              >
                Fund Request
                {isFundRequestReportData && (
                  <span className="team-tab-count">
                    {fundRequestRows.length}
                  </span>
                )}
              </button>
              <button
                className={`team-tab${activeTab === "withdraw" ? " active" : ""}`}
                onClick={handleWithdrawTabClick}
              >
                Withdraw Report
                {isWithdrawReportData && (
                  <span className="team-tab-count">{withdrawRows.length}</span>
                )}
              </button>
            </div>

            {activeTab === "direct" && (
              <>
                <div className="filters-row">
                  <div className="field">
                    <label>From Date</label>
                    <input
                      type="date"
                      value={directFromDate}
                      onChange={(e) => setDirectFromDate(e.target.value)}
                    />
                  </div>
                  <div className="field">
                    <label>To Date</label>
                    <input
                      type="date"
                      value={directToDate}
                      onChange={(e) => setDirectToDate(e.target.value)}
                    />
                  </div>
                  <div className="field field-54854">
                    <button
                      className="btn-sm primary"
                      onClick={handleDirectSearch}
                    >
                      Search
                    </button>
                  </div>
                  <div className="field field-54854">
                    <button className="btn-sm" onClick={handleDirectReset}>
                      Reset
                    </button>
                  </div>
                </div>
                <div className="card">
                  <div className="table-scroll">
                    <table>
                      <thead>
                        <tr>
                          <th>S.No</th>
                          <th>User ID</th>
                          <th>User Name</th>
                          <th>Activation Date</th>
                          <th>Activation Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isDirectLoading ? (
                          <tr>
                            <td colSpan={8} className="table-empty">
                              Loading direct team...
                            </td>
                          </tr>
                        ) : directRows.length === 0 ? (
                          <tr>
                            <td colSpan={8} className="table-empty">
                              No direct team members found.
                            </td>
                          </tr>
                        ) : (
                          directPaginated.map((member, idx) => (
                            <tr key={`${member.userId}-${idx}`}>
                              <td className="mono">
                                {(directPage - 1) * PAGE_SIZE + idx + 1}
                              </td>
                              <td className="mono">{member.strUserId}</td>
                              <td className="strong">{member.username}</td>
                              <td>{member.activationDate || "-"}</td>
                              <td className="mono">
                                {formatAmount(member.amount)}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <Pagination
                    currentPage={directPage}
                    totalItems={directRows.length}
                    pageSize={PAGE_SIZE}
                    onPageChange={setDirectPage}
                  />
                </div>
              </>
            )}

            {activeTab === "sponser" && (
              <>
                <div className="filters-row">
                  <div className="field">
                    <label>From Date</label>
                    <input
                      type="date"
                      value={sponserFromDate}
                      onChange={(e) => setSponserFromDate(e.target.value)}
                    />
                  </div>
                  <div className="field">
                    <label>To Date</label>
                    <input
                      type="date"
                      value={sponserToDate}
                      onChange={(e) => setSponserToDate(e.target.value)}
                    />
                  </div>
                  <div className="field">
                    <label>Level</label>
                    <select
                      value={sponserLevelNo}
                      onChange={(e) =>
                        setSponserLevelNo(Number(e.target.value))
                      }
                    >
                      <option value={0}>All Levels</option>
                      {LEVEL_OPTIONS.map((lvl) => (
                        <option key={lvl} value={lvl}>
                          Level {lvl}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="field">
                    <label>Search User ID</label>
                    <input
                      type="text"
                      placeholder="Enter user ID"
                      value={sponserSearchUserId}
                      onChange={(e) => setSponserSearchUserId(e.target.value)}
                    />
                  </div>
                  <div className="field field-54854">
                    <button
                      className="btn-sm primary"
                      onClick={handleSponserSearch}
                    >
                      Search
                    </button>
                  </div>
                  <div className="field field-54854">
                    <button className="btn-sm" onClick={handleSponserReset}>
                      Reset
                    </button>
                  </div>
                </div>
                <div className="card">
                  <div className="table-scroll">
                    <table>
                      <thead>
                        <tr>
                          <th>S.No</th>
                          <th>User Name</th>
                          <th>Mobile</th>
                          <th>Register Date</th>
                          <th>Activate Date</th>
                          <th>Status</th>
                          <th>Sponser Name</th>

                          <th>Level</th>
                          <th>Direct Team(Active / Deactive)</th>
                          <th>Total Team(Active / Deactive)</th>
                          <th>Self Business</th>
                          <th>Direct Business</th>
                          <th>Team Business</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isSponserLoading ? (
                          <tr>
                            <td colSpan={14} className="table-empty">
                              Loading sponsor team...
                            </td>
                          </tr>
                        ) : sponserRows.length === 0 ? (
                          <tr>
                            <td colSpan={14} className="table-empty">
                              No sponsor team members found.
                            </td>
                          </tr>
                        ) : (
                          sponserPaginated.map((member, idx) => (
                            <tr key={`${member.userId}-${idx}`}>
                              <td className="mono">
                                {(sponserPage - 1) * PAGE_SIZE + idx + 1}
                              </td>
                              <td className="strong">
                                {member.username}
                                <br />
                                <span>
                                  [{member.uPrefix}
                                  {member.userId}]
                                </span>
                              </td>
                              <td className="mono">{member.mobileNo}</td>
                              <td>{member.registerDate || "-"}</td>
                              <td>
                                {member.activationDate &&
                                member.activationDate !== "1900-01-01"
                                  ? member.activationDate
                                  : "-"}
                              </td>
                              <td>
                                <StatusBadge status={member.status} />
                              </td>
                              <td>
                                {member.sponserName} <br />
                                <span>
                                  [{member.sPrefix}
                                  {member.sponsoredId}]
                                </span>
                              </td>

                              <td>{member.levelNo}</td>
                              <td className="mono">
                                {member.activeDirect} / {member.deActiveDirect}
                              </td>
                              <td className="mono">
                                {member.totalActiveTeam} /
                                {member.totalDeActiveTeam}
                              </td>
                              <td className="mono">
                                {formatAmount(member.selfBusiness)}
                              </td>
                              <td className="mono">
                                {formatAmount(member.directBusiness)}
                              </td>
                              <td className="mono">
                                {formatAmount(member.teamBusiness)}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <Pagination
                    currentPage={sponserPage}
                    totalItems={sponserRows.length}
                    pageSize={PAGE_SIZE}
                    onPageChange={setSponserPage}
                  />
                </div>
              </>
            )}

            {activeTab === "income" && (
              <>
                <div className="filters-row">
                  <div className="field">
                    <label>From Date</label>
                    <input
                      type="date"
                      value={incomeFromDate}
                      onChange={(e) => setIncomeFromDate(e.target.value)}
                    />
                  </div>
                  <div className="field">
                    <label>To Date</label>
                    <input
                      type="date"
                      value={incomeToDate}
                      onChange={(e) => setIncomeToDate(e.target.value)}
                    />
                  </div>
                  <div className="field field-54854">
                    <button
                      className="btn-sm primary"
                      onClick={handleIncomeSearch}
                    >
                      Search
                    </button>
                  </div>
                  <div className="field field-54854">
                    <button className="btn-sm" onClick={handleIncomeReset}>
                      Reset
                    </button>
                  </div>
                </div>
                <div className="card">
                  <div className="table-scroll">
                    <table>
                      <thead>
                        <tr>
                          <th>S.No</th>
                          <th>From User Id</th>
                          <th>To User Id</th>
                          <th>TID</th>
                          <th>Income Name</th>
                          <th>Level No</th>

                          <th>Credit Amount</th>
                          <th>Entry Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isIncomeLoading ? (
                          <tr>
                            <td colSpan={11} className="table-empty">
                              Loading income report...
                            </td>
                          </tr>
                        ) : incomeRows.length === 0 ? (
                          <tr>
                            <td colSpan={11} className="table-empty">
                              No income records found.
                            </td>
                          </tr>
                        ) : (
                          incomePaginated.map((item, idx) => (
                            <tr key={idx}>
                              <td className="mono">
                                {(incomePage - 1) * PAGE_SIZE + idx + 1}
                              </td>
                              <td className="mono">
                                {item.fromName}
                                <br />[{item.fromUserId}]
                              </td>
                              <td className="mono">
                                {item.toName}
                                <br />[{item.toUserId}]
                              </td>
                              <td className="mono">{item.tid}</td>
                              <td className="strong">{item.incomeName}</td>
                              <td>{item.levelNo}</td>

                              <td className="mono">
                                {formatAmount(item.creditedAmount)}
                              </td>
                              <td>{item.entryDate}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <Pagination
                    currentPage={incomePage}
                    totalItems={incomeRows.length}
                    pageSize={PAGE_SIZE}
                    onPageChange={setIncomePage}
                  />
                </div>
              </>
            )}

            {activeTab === "fundRequest" && (
              <>
                <div className="filters-row">
                  <div className="field">
                    <label>From Date</label>
                    <input
                      type="date"
                      value={fundRequestFromDate}
                      onChange={(e) => setFundRequestFromDate(e.target.value)}
                    />
                  </div>
                  <div className="field">
                    <label>To Date</label>
                    <input
                      type="date"
                      value={fundRequestToDate}
                      onChange={(e) => setFundRequestToDate(e.target.value)}
                    />
                  </div>
                  <div className="field">
                    <label>Account No</label>
                    <input
                      type="text"
                      placeholder="Enter account number"
                      value={fundRequestAccountNo}
                      onChange={(e) => setFundRequestAccountNo(e.target.value)}
                    />
                  </div>
                  <div className="field field-54854">
                    <button
                      className="btn-sm primary"
                      onClick={handleFundRequestSearch}
                    >
                      Search
                    </button>
                  </div>
                  <div className="field field-54854">
                    <button className="btn-sm" onClick={handleFundRequestReset}>
                      Reset
                    </button>
                  </div>
                </div>
                <div className="card">
                  <div className="table-scroll">
                    <table>
                      <thead>
                        <tr>
                          <th>S.No</th>
                          <th>User</th>
                          <th>Bank</th>
                          <th>Account No</th>
                          <th>Mode</th>
                          <th>Transaction ID</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Entry Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isFundRequestLoading ? (
                          <tr>
                            <td colSpan={9} className="table-empty">
                              Loading fund request report...
                            </td>
                          </tr>
                        ) : fundRequestRows.length === 0 ? (
                          <tr>
                            <td colSpan={9} className="table-empty">
                              No fund request records found.
                            </td>
                          </tr>
                        ) : (
                          fundRequestPaginated.map((item, idx) => (
                            <tr key={item.paymentId ?? idx}>
                              <td className="mono">
                                {(fundRequestPage - 1) * PAGE_SIZE + idx + 1}
                              </td>
                              <td>
                                {item.userName}
                                <br />
                                <span>{item.userMobile}</span>
                              </td>
                              <td>{item.bank}</td>
                              <td className="mono">{item.accountNo}</td>
                              <td>{item.mode}</td>
                              <td className="mono">{item.transactionId}</td>
                              <td className="mono">
                                {formatAmount(item.amount)}
                              </td>
                              <td>
                                <span
                                  className={`badge ${
                                    String(item.status).toLowerCase() ===
                                    "accepted"
                                      ? "green"
                                      : String(item.status).toLowerCase() ===
                                          "rejected"
                                        ? "red"
                                        : "gold"
                                  }`}
                                >
                                  {item.status || "-"}
                                </span>
                              </td>
                              <td>{item.entryDate}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <Pagination
                    currentPage={fundRequestPage}
                    totalItems={fundRequestRows.length}
                    pageSize={PAGE_SIZE}
                    onPageChange={setFundRequestPage}
                  />
                </div>
              </>
            )}

            {activeTab === "withdraw" && (
              <>
                <div className="filters-row">
                  <div className="field">
                    <label>From Date</label>
                    <input
                      type="date"
                      value={withdrawFromDate}
                      onChange={(e) => setWithdrawFromDate(e.target.value)}
                    />
                  </div>
                  <div className="field">
                    <label>To Date</label>
                    <input
                      type="date"
                      value={withdrawToDate}
                      onChange={(e) => setWithdrawToDate(e.target.value)}
                    />
                  </div>
                  <div className="field field-54854">
                    <button
                      className="btn-sm primary"
                      onClick={handleWithdrawSearch}
                    >
                      Search
                    </button>
                  </div>
                  <div className="field field-54854">
                    <button className="btn-sm" onClick={handleWithdrawReset}>
                      Reset
                    </button>
                  </div>
                </div>
                <div className="card">
                  <div className="table-scroll">
                    <table>
                      <thead>
                        <tr>
                          <th>S.No</th>
                          <th>Transaction ID</th>
                          <th>Bank</th>
                          <th>Account No</th>
                          <th>Account Holder</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Entry Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isWithdrawLoading ? (
                          <tr>
                            <td colSpan={9} className="table-empty">
                              Loading withdraw report...
                            </td>
                          </tr>
                        ) : withdrawRows.length === 0 ? (
                          <tr>
                            <td colSpan={9} className="table-empty">
                              No withdraw records found.
                            </td>
                          </tr>
                        ) : (
                          withdrawPaginated.map((item, idx) => (
                            <tr key={item.tid ?? idx}>
                              <td className="mono">
                                {(withdrawPage - 1) * PAGE_SIZE + idx + 1}
                              </td>
                              <td className="mono">{item.transactionID}</td>
                              <td>{item.optional1}</td>
                              <td className="mono">{item.account}</td>
                              <td>{item.optional2}</td>

                              <td className="mono">
                                {formatAmount(item.amount)}
                              </td>
                              <td>
                                <span
                                  className={`badge ${
                                    String(item.type_).toLowerCase() ===
                                    "success"
                                      ? "green"
                                      : String(item.type_).toLowerCase() ===
                                          "failed"
                                        ? "red"
                                        : "gold"
                                  }`}
                                >
                                  {item.type_ || "-"}
                                </span>
                              </td>
                              <td>{item.entryDate}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <Pagination
                    currentPage={withdrawPage}
                    totalItems={withdrawRows.length}
                    pageSize={PAGE_SIZE}
                    onPageChange={setWithdrawPage}
                  />
                </div>
              </>
            )}
          </div>
        </section>
      </div>
    </>
  );
}
